import { useEffect, useMemo } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import { m } from 'framer-motion';

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { ForbiddenIllustration } from 'src/assets/illustrations';

import { varBounce, MotionContainer } from 'src/components/animate';
import { useAuthContext } from '../hooks';
import { useRouter } from 'src/routes/hooks/use-router';
import { paths } from 'src/routes/paths';
import { Permission } from 'src/assets/data/permissions';

// ----------------------------------------------------------------------

export type PermissionBasedGuardProp = {
  sx?: SxProps<Theme>;
  hasContent?: boolean;
  acceptPermissions: Permission[];
  children: React.ReactNode;
  isDashboard?: boolean;
};

const AVAILABLE_PAGES = [
  { path: paths.dashboard.invoices, permissions: [Permission.ReadInvoice] },
  { path: paths.dashboard.customers, permissions: [Permission.ReadCustomer] },
  { path: paths.dashboard.payments.paymentsManagement, permissions: [Permission.ReadPayment] },
  {
    path: paths.dashboard.payments.directDebitSubscriptions,
    permissions: [Permission.ReadPayment],
  },
  { path: paths.dashboard.reports, permissions: [Permission.ReadReport] },
  { path: paths.dashboard.users, permissions: [Permission.ReadUser] },
  { path: paths.dashboard.clients.client, permissions: [Permission.ReadClient] },
  { path: paths.dashboard.clients.serviceLine, permissions: [Permission.ReadClient] },
  { path: paths.dashboard.webhooks.subscriptions, permissions: [Permission.ReadWebhooks] },
  { path: paths.dashboard.webhooks.history, permissions: [Permission.ReadWebhooksHistory] },
  { path: paths.dashboard.settings.oidcClient, permissions: [Permission.ReadOidcClient] },
];

export function PermissionBasedGuard({
  sx,
  children,
  hasContent,
  acceptPermissions,
  isDashboard = false,
}: PermissionBasedGuardProp) {
  const { user } = useAuthContext();
  const router = useRouter();

  const hasPermission =
    acceptPermissions &&
    acceptPermissions.length > 0 &&
    acceptPermissions.some((permission) => user?.profile.permissions?.includes(permission));

  const redirectPage = useMemo(() => {
    if (!hasPermission && isDashboard && user?.profile?.permissions) {
      return AVAILABLE_PAGES.find((page) =>
        page.permissions.some((permission) => user.profile.permissions!.includes(permission))
      );
    }
    return undefined;
  }, [hasPermission, isDashboard, user?.profile?.permissions]);

  useEffect(() => {
    if (redirectPage) {
      router.replace(redirectPage.path);
    }
  }, [redirectPage, router]);

  if (!hasPermission) {
    // Redirect is pending — render nothing to avoid a "Permission denied" flash.
    if (redirectPage) return null;

    return hasContent ? (
      <Container
        component={MotionContainer}
        sx={[
          {
            textAlign: 'center',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <m.div variants={varBounce('in')}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            Permission denied
          </Typography>
        </m.div>

        <m.div variants={varBounce('in')}>
          <Typography sx={{ color: 'text.secondary' }}>
            You do not have permission to access this page.
          </Typography>
        </m.div>

        <m.div variants={varBounce('in')}>
          <ForbiddenIllustration sx={{ my: { xs: 5, sm: 10 } }} />
        </m.div>
      </Container>
    ) : null;
  }

  return <> {children} </>;
}
